import { getCategoryById } from '../Entities/Categories/categories.reducer';
import { AppState } from '../reducers';
import { Action } from 'redux';
import { isType } from 'typescript-fsa';
import {
  resetStreamConfig,
  setStreamConfigCategory,
  setStreamConfigLoading,
  updateStreamConfigActiveFilterItems,
  updateStreamConfigFilterItems,
  updateStreamConfigFilterNames,
  updateStreamConfigFilters,
  updateStreamConfigProductCount,
  toggleBlacklistForFilter,
  setStreamConfigMode,
  toggleIsInheritedByChildCategories,
} from './stream-category-config.actions';
import * as Immutable from 'seamless-immutable';
import { ImmutableObject } from 'seamless-immutable';
import * as R from 'ramda';
import { AttributeFilter } from '../Entities/Stories/stories.reducer';
import { resetActiveStory } from '../StoryEdit/story-edit.actions';
import { groupByFilterId, mapIds } from './stream-category-config.saga';

export enum ConfigurationMode {
  Create = 'CREATE',
  Edit = 'EDIT',
}

export interface StreamCategoryConfig {
  categoryId?: number;
  appId?: number;
  availableFilters: {
    [key: string]: FilterItem[];
  };
  activeFilterItems: FilterItem[];
  filterNames: {
    [key: string]: string;
  };
  blacklistedFilters: string[];
  productCount: number;
  loading: boolean;
  mode: ConfigurationMode;
  isInheritedByChildCategories: boolean;
}

export interface FilterItem {
  name: string;
  id: number;
  count?: number;
  hex?: string;
  filterId: string;
  attributeGroupId: number;
}

export type StreamCategoryConfigState = StreamCategoryConfig &
  ImmutableObject<StreamCategoryConfig>;

const initialState = Immutable.from<StreamCategoryConfig>({
  availableFilters: {},
  activeFilterItems: [],
  filterNames: {},
  blacklistedFilters: [],
  productCount: 0,
  loading: false,
  mode: ConfigurationMode.Create,
  isInheritedByChildCategories: false,
});

export default function reduce(
  state: StreamCategoryConfigState = initialState,
  action: Action
) {
  if (isType(action, setStreamConfigCategory)) {
    const {
      payload: { categoryId, appId },
    } = action;
    return state.merge({
      categoryId,
      appId,
    });
  }

  if (isType(action, resetStreamConfig)) {
    return initialState;
  }

  if (isType(action, updateStreamConfigFilterItems)) {
    const {
      payload: { filterId, filterItems },
    } = action;
    return state.merge(
      {
        availableFilters: {
          [filterId]: filterItems,
        } as any,
      },
      { deep: true }
    );
  }

  if (isType(action, updateStreamConfigFilters)) {
    const {
      payload: { availableFilters },
    } = action;
    return state.merge(
      {
        availableFilters,
      },
      { deep: true }
    );
  }

  if (isType(action, updateStreamConfigFilterNames)) {
    const {
      payload: { filterNames },
    } = action;
    return state.merge(
      {
        filterNames,
      },
      { deep: true }
    );
  }

  if (isType(action, updateStreamConfigActiveFilterItems)) {
    const {
      payload: { filterItems },
    } = action;
    return state.merge({
      activeFilterItems: filterItems,
    });
  }

  if (isType(action, updateStreamConfigProductCount)) {
    const {
      payload: { productCount },
    } = action;
    return state.merge({
      productCount,
    });
  }

  if (isType(action, toggleBlacklistForFilter)) {
    const {
      payload: { filterId },
    } = action;

    if (state.blacklistedFilters.includes(filterId)) {
      return state.merge({
        blacklistedFilters: state.blacklistedFilters.filter(
          f => f !== filterId
        ),
      });
    }

    return state.merge({
      blacklistedFilters: [...state.blacklistedFilters, filterId],
    });
  }

  if (isType(action, toggleIsInheritedByChildCategories)) {
    return state.merge({
      isInheritedByChildCategories: !state.isInheritedByChildCategories,
    });
  }

  if (isType(action, setStreamConfigLoading)) {
    const {
      payload: { loading },
    } = action;
    return state.merge({
      loading,
    });
  }

  if (isType(action, resetActiveStory)) {
    return initialState;
  }

  if (isType(action, setStreamConfigMode)) {
    return state.merge({ mode: action.payload.mode });
  }

  return state;
}

export function getStreamCategoryConfig(state: AppState) {
  const { streamCategoryConfig } = state;
  return streamCategoryConfig;
}

export function getStreamCategoryConfigCategoryId(state: AppState) {
  const config = getStreamCategoryConfig(state);
  return config.categoryId;
}

export function getStreamCategoryConfigAppId(state: AppState) {
  const config = getStreamCategoryConfig(state);
  return config.appId;
}

export function getStreamCategoryConfigCategory(state: AppState) {
  const categoryId = getStreamCategoryConfigCategoryId(state);
  const appId = getStreamCategoryConfigAppId(state);
  if (appId && categoryId) {
    return getCategoryById(state, appId, categoryId);
  }
  return false;
}

export function getAvailableFilters(state: AppState) {
  const { availableFilters } = getStreamCategoryConfig(state);
  const activeFilterItems = getStreamCategoryConfigActiveFilterItems(state);
  const activeFilterIds = R.map(
    (activeFilterItem: FilterItem) => activeFilterItem.id,
    activeFilterItems
  );
  return R.mapObjIndexed((filterItems: FilterItem[]) => {
    return R.reject(filterItem => {
      return R.indexOf(filterItem.id, activeFilterIds) > -1;
    }, filterItems);
  }, availableFilters);
}

export function getStreamCategoryConfigActiveFilterItems(state: AppState) {
  const { activeFilterItems } = getStreamCategoryConfig(state);
  return activeFilterItems;
}

export function getStreamCategoryConfigFilterNames(state: AppState) {
  const { filterNames } = getStreamCategoryConfig(state);
  return filterNames;
}

export function getStreamCategoryConfigProductCount(state: AppState) {
  const { productCount } = getStreamCategoryConfig(state);
  return productCount;
}

export function getStreamCategoryConfigLoading(state: AppState) {
  const { loading } = getStreamCategoryConfig(state);
  return loading;
}

export function getBlacklistedFilters(state: AppState) {
  const { blacklistedFilters } = getStreamCategoryConfig(state);

  return blacklistedFilters;
}

export function getIsInheritedByChildCategories(state: AppState) {
  const { isInheritedByChildCategories } = getStreamCategoryConfig(state);

  return isInheritedByChildCategories;
}

export function getSelectedFilters(state: AppState) {
  const activeFilterItems = getStreamCategoryConfigActiveFilterItems(state);
  const availableFilters = getAvailableFilters(state);
  const activeFilterIds = mapIds(groupByFilterId(activeFilterItems));
  const blacklistedFilters = getBlacklistedFilters(state);

  const getInvertForFilterType = (type: string) => {
    const availableFiltersForType = availableFilters[type] || [];

    return availableFiltersForType.reduce((acc, cur) => {
      if (activeFilterIds[type] && activeFilterIds[type].includes(cur.id)) {
        return acc;
      }

      return [...acc, cur];
    }, []);
  };

  return Object.keys(availableFilters).reduce((acc, cur) => {
    if (blacklistedFilters.includes(cur)) {
      return [...acc, ...getInvertForFilterType(cur)];
    }

    return [...acc, ...activeFilterItems.filter(i => i.filterId === cur)];
  }, []);
}

export function getConfigurationMode(state: AppState) {
  return state.streamCategoryConfig.mode;
}

const byAttributeGroupId = R.groupBy((filterItem: FilterItem) => {
  return '' + filterItem.attributeGroupId;
});

export function mapFilterItemsByAttrGroupId(
  state: AppState
): {
  [key: number]: FilterItem[];
} {
  const selectedFilterItems = getSelectedFilters(state);

  return byAttributeGroupId(selectedFilterItems);
}

export function mapToAttributeFilterList(state: AppState): AttributeFilter[] {
  const byGroupId = mapFilterItemsByAttrGroupId(state);
  const attributeFilterList: AttributeFilter[] = [];

  R.forEachObjIndexed((filterItems: FilterItem[], key) => {
    const mapped = filterItems.reduce(
      (acc, actual) => {
        acc.attributeIds.push(+actual.id);
        return acc;
      },
      {
        attributeGroupId: +key,
        attributeIds: [] as number[],
      }
    );
    attributeFilterList.push(mapped);
  }, byGroupId);

  return attributeFilterList;
}



// WEBPACK FOOTER //
// ./src/store/StreamCategoryConfig/stream-category-config.reducer.ts